import { action } from '@play-co/replicant';
import type { MutableState } from '../State';
import createActions from '../config/actions/createActions';
import features from '../ruleset/features';
import { getRandomItemFromArray } from '../utils/random';
import {
  dreamDriftCanStartInstance,
  dreamDriftGetFinalNode,
  dreamDriftGetInstanceRemainingTime,
  dreamDriftHasCompletedCurrentEvent,
} from './dreamDrift.getters';
import {
  dreamDriftFinishLevel,
  dreamDriftResolveInstance,
  dreamDriftStartInstance,
  dreamDriftStartLevel,
} from './dreamDrift.modifiers';
import {
  dreamDriftHeroes,
  dreamDriftInstanceDuration,
  dreamDriftSchedule,
} from './dreamDrift.ruleset';

/**
 * Get the global availability issue that should interrupt a cheat workflow.
 *
 * @param state Player state to inspect.
 * @param now Server-authoritative timestamp.
 * @returns Alertable availability issue, or undefined when cheats may proceed.
 */
function dreamDriftGetCheatAvailabilityIssue(
  state: MutableState,
  now: number,
): string | undefined {
  // Disabled treatment players cannot use Dream Drift cheats.
  if (!features(state).dreamDrift) return 'Dream Drift feature is disabled';

  // Dream Drift cheats require an ongoing scheduled event window.
  if (!dreamDriftSchedule.getCurrentEvent(now)) {
    return 'No active Dream Drift event';
  }

  // Global availability checks passed without an alertable issue.
  return undefined;
}

/** Development-only actions for exercising Dream Drift backend states. */
export const dreamDriftCheats = createActions({
  /** Delete Dream Drift progress and event promo history for the user. */
  cheat_dreamDriftReset: action((state) => {
    // Remove both feature-seen and instance state from the real user state.
    delete state.dreamDrift;

    // Allow the event introduction and lifetime FTUE to be shown again.
    delete state.promo.dreamDriftEventPromo;

    // Allow treatment users to receive the same event's automatic introduction again.
    delete state.promoGroups?.ftueFeatures?.items.dreamDrift;
  }),

  /** Advance one non-final Dream Drift node through the production lifecycle. */
  cheat_dreamDriftAdvanceLevel: action((state, _args: void, api) => {
    // Use one server timestamp for the complete level transition.
    const now = api.date.now();

    // Global availability failures should be surfaced to the cheats UI.
    const availabilityIssue = dreamDriftGetCheatAvailabilityIssue(state, now);
    if (availabilityIssue) {
      return {
        issue: availabilityIssue,
        level: undefined,
      };
    }

    // Completed events must retain their terminal state and rewards.
    if (dreamDriftHasCompletedCurrentEvent(state, now)) {
      return {
        issue: 'Dream Drift event is already completed',
        level: undefined,
      };
    }

    // Read the current instance after global availability checks pass.
    let instance = state.dreamDrift?.instance;

    // Create or replace the instance when the production lifecycle permits it.
    if (!instance || dreamDriftCanStartInstance(state, now)) {
      const hero = getRandomItemFromArray(
        [...dreamDriftHeroes],
        api.math.random,
      );
      const startedAt = dreamDriftStartInstance(state, { now, hero });
      instance = state.dreamDrift?.instance;
      if (startedAt === undefined || !instance) {
        return {
          issue: 'Cannot start instance',
          level: undefined,
        };
      }
    }

    // Resolve the active attempt or the next node after stored progress.
    const node = instance.playing || instance.progress + 1;

    // Preserve the final puzzle and reward presentation for normal gameplay.
    if (node >= dreamDriftGetFinalNode()) {
      return {
        issue: 'Final Dream Drift level must be played normally',
        level: undefined,
      };
    }

    // Idle instances start the next validated node through the production modifier.
    if (instance.playing === 0) {
      // Start the validated next node through the production modifier.
      const started = dreamDriftStartLevel(state, {
        startedAt: instance.startedAt,
        node,
        now,
      });
      if (!started) {
        return {
          issue: 'Cannot start level',
          level: undefined,
        };
      }
    }

    // Finish the started node as a win through the real progression modifier.
    const finishResult = dreamDriftFinishLevel(state, {
      startedAt: instance.startedAt,
      result: 'win',
    });
    if (!finishResult) {
      return {
        issue: 'Cannot finish level',
        level: undefined,
      };
    }

    // Read the updated real instance for level output.
    const updatedInstance = state.dreamDrift?.instance;
    if (!updatedInstance) {
      return {
        issue: 'Instance missing after level finish',
        level: undefined,
      };
    }

    // Return the applied level result without granting deferred milestone rewards.
    return {
      issue: undefined,
      level: {
        node,
        rewardGems: 0,
        currentLevel: Math.min(
          updatedInstance.progress + 1,
          dreamDriftGetFinalNode(),
        ),
      },
    };
  }),

  /** Lose the active or next Dream Drift node through the production lifecycle. */
  cheat_dreamDriftLoseLevel: action((state, _args: void, api) => {
    // Use one server timestamp for the complete level transition.
    const now = api.date.now();

    // Global availability failures should be surfaced to the cheats UI.
    const availabilityIssue = dreamDriftGetCheatAvailabilityIssue(state, now);
    if (availabilityIssue) {
      return {
        issue: availabilityIssue,
        level: undefined,
      };
    }

    // Completed events must retain their terminal state and rewards.
    if (dreamDriftHasCompletedCurrentEvent(state, now)) {
      return {
        issue: 'Dream Drift event is already completed',
        level: undefined,
      };
    }

    // Read the current instance after global availability checks pass.
    const instance = state.dreamDrift?.instance;

    // Losing requires an ongoing instance with remaining Dream Meter time.
    const remainingTime = instance
      ? dreamDriftGetInstanceRemainingTime(state, now)
      : 0;
    if (!instance || instance.result || remainingTime === 0) {
      return {
        issue: 'No running Dream Drift instance',
        level: undefined,
      };
    }

    // Resolve the active attempt or the next node after stored progress.
    const node = instance.playing || instance.progress + 1;

    // Idle instances start the next validated node through the production modifier.
    if (instance.playing === 0) {
      // Start the validated next node through the production modifier.
      const started = dreamDriftStartLevel(state, {
        startedAt: instance.startedAt,
        node,
        now,
      });
      if (!started) {
        return {
          issue: 'Cannot start level',
          level: undefined,
        };
      }
    }

    // Finish the started node as a loss through the real progression modifier.
    const finishResult = dreamDriftFinishLevel(state, {
      startedAt: instance.startedAt,
      result: 'lose',
    });
    if (!finishResult) {
      return {
        issue: 'Cannot finish level',
        level: undefined,
      };
    }

    // Read the checkpoint restored on the real instance by the loss.
    const updatedInstance = state.dreamDrift?.instance;
    if (!updatedInstance) {
      return {
        issue: 'Instance missing after level finish',
        level: undefined,
      };
    }

    // Return the applied loss and next playable level from real user state.
    return {
      issue: undefined,
      level: {
        node,
        rewardGems: 0,
        currentLevel: Math.min(
          updatedInstance.progress + 1,
          dreamDriftGetFinalNode(),
        ),
      },
    };
  }),

  /** Expire the current event instance through the production resolver. */
  cheat_dreamDriftExpireInstance: action((state, _args: void, api) => {
    // Global availability failures should be surfaced to the cheats UI.
    const now = api.date.now();
    const availabilityIssue = dreamDriftGetCheatAvailabilityIssue(state, now);
    if (availabilityIssue) return availabilityIssue;

    // Completed events cannot be replaced by an expired cheat instance.
    if (dreamDriftHasCompletedCurrentEvent(state, now)) {
      return 'Dream Drift event is already completed';
    }

    // Expiry requires an existing unfinished instance with time remaining.
    const instance = state.dreamDrift?.instance;
    const remainingTime = instance
      ? dreamDriftGetInstanceRemainingTime(state, now)
      : 0;
    if (!instance || instance.result || remainingTime === 0) {
      return 'No active Dream Drift instance';
    }

    // Resolve at the instance deadline without changing its event identity.
    const deadline = instance.startedAt + dreamDriftInstanceDuration;
    dreamDriftResolveInstance(state, deadline, api);

    // Expiry succeeded without an issue to display.
    return undefined;
  }),
});
